import { Form, Formik } from 'formik';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import Input from '@src/components/_shared/UI/Input';
import { useEffect, useState } from 'react';
import useNavigationStore from '@src/stores/navigationStore';
import useRegistrationStore from '@src/stores/registrationStore';
import styles from './Various.module.scss';
import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import BackButton from '@src/components/_shared/BackButton';
import Container from '@src/components/_shared/Container';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getReferencialData } from '@src/api/Referencial';
import { companyUpdate } from '@src/api/Registration';
import Loader from '@src/components/_shared/UI/Loader';
import PictureButton from '@src/components/_shared/UI/PictureButton';
import { leiSchema } from '@src/utilities/schemas';
import { handleApiError } from '@src/utilities/functions';

const FORM_STEPS = {
    VAT: 0,
    LEI: 1,
    FISCAL_OPTION: 2,
};

export default function Various() {
    const { setSection, setBlock } = useNavigationStore();
    const { setField, registration } = useRegistrationStore();
    const [currentStep, setCurrentStep] = useState(FORM_STEPS.VAT);
    const [submitted, setSubmitted] = useState<boolean>(false);

    const { data, isLoading } = useQuery({ 
        queryKey: [`taxOptions`],
        queryFn: () => getReferencialData({type: 'company/tax-options'})
    });

    const { basic, tax_option, lei, vat } = registration.company;
    const { mutate, isPending } = useMutation({
        mutationFn: () => companyUpdate({ value: { basic, tax_option, lei, vat }, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id }),
        onSuccess: () => {
            setBlock('Beneficiaries');
        },
        onError: (error: any) => handleApiError(error)
    });

    useEffect(() => {
        if (submitted) {
            mutate();
        }
        return () => {
            setSubmitted(false);
        }
    }, [submitted]);

    const _onGoBack = () => {
        setSection(3);
        setBlock('CompanyInformations');
    };

    const goToNextStep = () => {
        setCurrentStep((prevStep) => prevStep + 1);
    };

    const goToPreviousStep = () => {
        setCurrentStep((prevStep) => (prevStep > 0 ? prevStep - 1 : prevStep));
    };

    const _onChooseFiscalOption = (value: string) => {
        setField('company.tax_option', value);
        setSubmitted(true);
    }

    if (isLoading) {
        return (
            <Container>
                <Loader type="SPINNER" />
            </Container>
        )
    }

    return (
        <Container>
            {currentStep === FORM_STEPS.VAT && (
                <>
                    <BackButton onClick={_onGoBack}/>
                    <BlockTitle title="TVA intracommunautaire" />
                    <Subtitle content="Veuillez indiquer le numéro de la TVA intracommunautaire (facultatif)" />
                    <Formik
                        initialValues={{ 
                            vat: registration.company.vat || ''
                        }}
                        onSubmit={(values) => {
                            setField('company.vat', values.vat);
                            goToNextStep();
                        }}
                    >
                        {({ handleChange, handleSubmit, values }) => {
                            return(
                                <Form id="vatForm" className="registerForm" autoComplete="off">
                                    <Input
                                        title="TVA intracommunautaire"
                                        name="vat"
                                        id="vat"
                                        width="100%"
                                        placeholder="Ex : FR 53 157896342"
                                        type="text"
                                        value={values.vat}
                                        onChange={handleChange('vat')}
                                        clearInput={() =>handleChange('vat')('')}
                                    />
                                    <ValidateButton title="Continuer" onClick={handleSubmit} />
                                </Form>
                            )
                        }}
                    </Formik>
                </>
            )}
            {currentStep === FORM_STEPS.LEI && (
                <>
                    <BackButton onClick={() => goToPreviousStep()}/>
                    <BlockTitle title="Identifiant d'entité juridique" />
                    <Subtitle content="Veuillez indiquer le numéro LEI" />
                    <Formik
                        initialValues={{ 
                            lei: registration.company.lei || ''
                        }}
                        onSubmit={(values) => {
                            setField('company.lei', values.lei.toString());
                            goToNextStep();
                        }}
                        validationSchema={leiSchema}
                    >
                        {({ handleChange, handleSubmit, values, isValid, errors }) => {
                            return(
                                <Form id="leiForm" className="registerForm" autoComplete="off">
                                    <Input
                                        title="Numéro LEI"
                                        name="lei"
                                        id="lei"
                                        width="100%"
                                        placeholder="Ex : 30613890001294568240"
                                        type="text"
                                        value={values.lei}
                                        onChange={handleChange('lei')}
                                        clearInput={() =>handleChange('lei')('')}
                                        hasError={!!(errors.lei)}
                                        errorMessage={errors.lei}
                                    />
                                    <ValidateButton title="Continuer" disabled={!isValid || isPending || !values.lei} onClick={handleSubmit} />
                                </Form>
                            )
                        }}
                    </Formik>
                </>
            )}
            {currentStep === FORM_STEPS.FISCAL_OPTION && (
                <>
                    <BackButton onClick={goToPreviousStep}/>
                    <BlockTitle title="Option fiscale" />
                    <Subtitle content="Veuillez choisir une des deux options :" />
                    {isLoading || isPending ?
                        <Loader type='SPINNER' />
                        :
                        <div className={styles.buttonContainer}>
                            <PictureButton type='IrOption' onClick={() => _onChooseFiscalOption(Object.keys(data)[0])} />
                            <PictureButton type='IsOption' onClick={() => _onChooseFiscalOption(Object.keys(data)[1])} />
                        </div>
                    }
                </>
            )}
        </Container>
    );
}
